/**
 * Utility functions for share functionality
 */
import { warn } from '@/utils/logger';
import { isNativePlatform } from '@/lib/platform';

/**
 * Type guard for Axios errors
 */
export interface AxiosErrorResponse {
  response?: {
    status?: number;
    data?: unknown;
  };
}

export function isAxiosError(error: unknown): error is AxiosErrorResponse {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as AxiosErrorResponse).response === 'object'
  );
}

/**
 * Extract HTTP status code from error
 */
export function getErrorStatus(error: unknown): number | undefined {
  if (isAxiosError(error)) {
    return error.response?.status;
  }
  return undefined;
}

/**
 * Copy text to clipboard.
 *
 * On native (Capacitor), uses @capacitor/clipboard which calls UIPasteboard
 * (iOS) / ClipboardManager (Android) directly — sidesteps WKWebView's
 * user-gesture quirks where navigator.clipboard.writeText sometimes rejects
 * even from a real tap event.
 *
 * On web, prefers navigator.clipboard.writeText (the modern Async Clipboard
 * API) and falls back to the deprecated document.execCommand('copy') dance
 * for very old browsers.
 */
export async function copyToClipboard(text: string): Promise<void> {
  if (isNativePlatform()) {
    try {
      const { Clipboard } = await import('@capacitor/clipboard');
      await Clipboard.write({ string: text });
      return;
    } catch (err) {
      warn('[copyToClipboard] @capacitor/clipboard failed, falling back to web APIs', err);
      // Fall through to navigator.clipboard / execCommand below.
    }
  }

  // Modern Async Clipboard API (preferred on web).
  if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch (err) {
      warn('[copyToClipboard] navigator.clipboard failed, trying execCommand fallback', err);
    }
  }

  // Last-resort fallback for very old browsers (no Async Clipboard API).
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);

    if (!successful) {
      throw new Error('execCommand copy failed');
    }
  } catch (err) {
    throw new Error(`Failed to copy to clipboard: ${err instanceof Error ? err.message : 'Unknown error'}`);
  }
}

/**
 * Validate share token format (basic validation)
 */
export function isValidShareToken(token: string | null | undefined): token is string {
  if (!token || typeof token !== 'string') {
    return false;
  }
  // Share tokens are 32 characters, base64url encoded
  // Allow some flexibility but ensure reasonable length
  return token.length >= 16 && token.length <= 64;
}

/**
 * Get user-friendly error message for share-related errors
 */
export function getShareErrorMessage(status: number | undefined): string {
  switch (status) {
    case 401:
      return 'This link has expired or is invalid.';
    case 403:
      return 'This link has expired or is invalid.';
    case 404:
      return 'This content could not be found.';
    case 429:
      return 'Too many requests. Please try again later.';
    case 500:
    case 502:
    case 503:
      return 'Server error. Please try again later.';
    default:
      return 'Unable to load this content. Please try again later.';
  }
}

